import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import { ComponentProps } from '../../ClientGetStartedModal.meta';
import { Radio } from '../../../CommonComponents/OPL/Radio/Radio';
import { Text } from '../../../CommonComponents/OPL/Text/Text';
import { FORM_SCHEMA, FormValues } from './LanguagePreference.meta';
import { ErrorState } from '../../../CommonComponents/OPL/AsyncStates/ErrorState/ErrorState';
import { Button } from '../../../CommonComponents/OPL/Button/Button';
import { ModalFooter } from '../../../CommonComponents/OPL/Modal/ModalFooter';
import {
  LanguagePreferenceData,
  useLanguagePreference,
} from '../../../TopNav/Consumer/components/LanguagePreference/useLanguagePreference';
import { Box } from '../../../CommonComponents/OPL/Box/Box';
import { HelperText } from '../../../CommonComponents/OPL/HelperText/HelperText';
import { useLocalizeMutation } from '../../../TopNav/api/useLocalizeMutation';
import { Alert } from '../../../CommonComponents/OPL/Alert/Alert';
import { LanguagePreferenceLoading } from './LanguagePreference.loading';
import { ContentWrapper } from '../../ClientGetStartedModal.styles';

export const LanguagePreference = ({ clientId, ...rest }: ComponentProps) => {
  const { isLoading, isError, refetch, data } = useLanguagePreference(clientId);

  if (isLoading) {
    return <LanguagePreferenceLoading />;
  }

  if (isError) {
    return <ErrorState onAction={refetch} m="56" mt="24" />;
  }

  if (!data) {
    return null;
  }

  return <LanguagePreferenceContent clientId={clientId} data={data} {...rest} />;
};

interface ContentProps extends ComponentProps {
  data: LanguagePreferenceData;
}

const LanguagePreferenceContent = ({ clientId, onPrevious, onNext, data }: ContentProps) => {
  const formBag = useForm<FormValues>({
    defaultValues: {
      languagePreference: null,
    },
    resolver: yupResolver(FORM_SCHEMA),
  });

  const {
    mutateAsync: switchLanguage,
    isLoading: isSwitchingLanguage,
    error: switchLanguageError,
  } = useLocalizeMutation();

  const onSubmit = async ({ languagePreference }: FormValues) => {
    try {
      if (languagePreference !== null) {
        await switchLanguage({ clientId, languagePreference });

        const selectedLanguageOption = data.languageOptions.find(
          ({ value }) => value === languagePreference,
        );

        if (selectedLanguageOption) {
          Localize.setLanguage(selectedLanguageOption.code);
        }

        onNext();
      }
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <form onSubmit={formBag.handleSubmit(onSubmit)} aria-label="Default language selection form">
      <ContentWrapper as="section">
        {switchLanguageError && (
          <Alert message={switchLanguageError.message} variant="error" mb="16" />
        )}

        <Text as="p" color="neutral.500" mb="16">
          Select your preferred language to display
        </Text>

        <Controller
          name="languagePreference"
          control={formBag.control}
          render={({ field: { value, name, onChange } }) => (
            <Box display="flex" flexDirection="column" css="gap: 8px">
              {data.languageOptions.map(option => (
                <Radio
                  key={option.value}
                  name={name}
                  checked={value === option.value}
                  onChange={() => onChange(option.value)}
                >
                  {option.text}
                </Radio>
              ))}
            </Box>
          )}
        />

        {formBag.formState.errors.languagePreference && (
          <HelperText variant="error" mt="4">
            {formBag.formState.errors.languagePreference.message}
          </HelperText>
        )}
      </ContentWrapper>

      <ModalFooter>
        {onPrevious && (
          <Button variant="secondary" onClick={onPrevious} leftIcon="fas fa-chevron-left" mr="auto">
            Back
          </Button>
        )}

        <Button type="submit" isLoading={isSwitchingLanguage}>
          Save
        </Button>
      </ModalFooter>
    </form>
  );
};
